import React, { useState, useEffect, useCallback } from 'react';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import SALayout from './SALayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, Megaphone, Info, AlertTriangle, CheckCircle, Rocket, Loader2 } from 'lucide-react';

const typeOptions = [
  { value: 'info', label: 'Info', icon: Info, color: 'bg-blue-100 text-blue-700' },
  { value: 'warning', label: 'Warning', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700' },
  { value: 'success', label: 'Success', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
  { value: 'release', label: 'New Release', icon: Rocket, color: 'bg-indigo-100 text-indigo-700' },
];

export default function SAAnnouncements() {
  const { apiFetch } = useSuperAdmin();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: '', message: '', type: 'info', expires_at: '' });

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/announcements');
      if (res.ok) setAnnouncements(await res.json());
    } catch {} finally { setLoading(false); }
  }, [apiFetch]);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        message: form.message.trim(),
        type: form.type,
        expires_at: form.expires_at || null
      };
      const path = editItem ? `/announcements/${editItem.id}` : '/announcements';
      const res = await apiFetch(path, {
        method: editItem ? 'PUT' : 'POST',
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(editItem ? 'Announcement updated' : 'Announcement published to all customers');
        setShowDialog(false);
        setEditItem(null);
        setForm({ title: '', message: '', type: 'info', expires_at: '' });
        fetchAnnouncements();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch { toast.error('Failed to save announcement'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement for all customers?')) return;
    try {
      const res = await apiFetch(`/announcements/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Announcement deleted'); fetchAnnouncements(); }
      else { toast.error('Failed to delete'); }
    } catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (item) => {
    try {
      const res = await apiFetch(`/announcements/${item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !item.is_active })
      });
      if (res.ok) fetchAnnouncements();
    } catch {}
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      title: item.title,
      message: item.message,
      type: item.type,
      expires_at: item.expires_at ? String(item.expires_at).split('T')[0] : ''
    });
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditItem(null);
    setForm({ title: '', message: '', type: 'info', expires_at: '' });
    setShowDialog(true);
  };

  return (
    <SALayout>
      <div className="space-y-6" data-testid="sa-announcements-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Megaphone className="w-6 h-6" /> Global Announcements
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Broadcast platform-wide messages to all customers (admins &amp; employees across every organization).
            </p>
          </div>
          <Button onClick={openCreate} data-testid="sa-create-announcement-btn">
            <Plus className="w-4 h-4 mr-2" /> New Announcement
          </Button>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : announcements.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Megaphone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No global announcements yet.</p>
              <p className="text-sm text-slate-400 mt-1">Create one to notify all customers using Inspiro Tracker.</p>
              <Button onClick={openCreate} variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" /> Create First Announcement
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {announcements.map(item => {
              const typeOpt = typeOptions.find(t => t.value === item.type) || typeOptions[0];
              const Icon = typeOpt.icon;
              const isExpired = item.expires_at && new Date(item.expires_at) < new Date();
              return (
                <Card key={item.id} className={!item.is_active || isExpired ? 'opacity-60' : ''} data-testid={`sa-announcement-${item.id}`}>
                  <CardContent className="p-4 flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeOpt.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{item.title}</h3>
                        <Badge variant={item.is_active ? 'default' : 'secondary'} className="text-xs">
                          {item.is_active ? (isExpired ? 'Expired' : 'Active') : 'Inactive'}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">{item.type}</Badge>
                      </div>
                      <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{item.message}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
                        <span>By {item.created_by_name || 'Super Admin'}</span>
                        <span>{new Date(item.created_at).toLocaleDateString()}</span>
                        {item.expires_at && <span>Expires: {new Date(item.expires_at).toLocaleDateString()}</span>}
                        <span>{item.dismiss_count || 0} dismissed</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Switch checked={item.is_active} onCheckedChange={() => toggleActive(item)} data-testid={`toggle-${item.id}`} />
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)} data-testid={`edit-${item.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(item.id)} data-testid={`delete-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) setEditItem(null); }}>
          <DialogContent data-testid="sa-announcement-dialog">
            <DialogHeader>
              <DialogTitle>{editItem ? 'Edit Announcement' : 'New Global Announcement'}</DialogTitle>
              <DialogDescription>
                This banner will appear at the top of the app for every user across every customer organization.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g., Scheduled Maintenance"
                  data-testid="sa-announcement-title"
                />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Describe the announcement..."
                  rows={3}
                  data-testid="sa-announcement-message"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                    <SelectTrigger data-testid="sa-announcement-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {typeOptions.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Expires on (optional)</Label>
                  <Input
                    type="date"
                    value={form.expires_at}
                    onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                    data-testid="sa-announcement-expires"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="sa-save-announcement-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editItem ? 'Update' : 'Publish'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SALayout>
  );
}
